import { useEffect, useState } from "react";
import { api, ApiError } from "./api";
import { t, virheTeksti } from "./kieli";

/*
 * «Hitsiä ei tehdä» — puuttuvan hitsin merkintä (158, Toni 21.9.2026).
 *
 * ⛔ MIKSI TÄMÄ ON ERI ASIA KUIN RIVIN «Ei toteudu». 152:n merkintä
 * kiinnittyy olemassa olevaan riviin. Mitattu pekilossa 21.9.2026:
 * linjalla jolla hitsit 1–7 on kirjattu, **hitsiä 8 ei ole olemassa** —
 * ja koko instanssissa vain 25 suunnitellulla rivillä on oikea numero
 * (355:llä numero on kirjaimellisesti sana «(suunniteltu)»). Useimmilla
 * linjoilla ei siis ole riviä johon merkitä, ja tämä luo sen.
 *
 * ⛔ SAMA KOMPONENTTI KUMPAANKIN PAIKKAAN. Toni nimesi kaksi paikkaa:
 * kenttäkirjaus (hitsaaja esittää) ja Selailun linjanäkymä (työnjohto
 * merkitsee itse). Kaksi kopiota samasta lomakkeesta erkanee — ja
 * perustelun pakollisuus on juuri se asia joka toisesta kopiosta
 * unohtuisi.
 */
export default function HitsiaEiTehda({ linjaId, linjaTunnus, sulje, valmis }: {
  linjaId: number;
  linjaTunnus?: string;
  sulje: () => void;
  valmis: (tulos: { id: number; tila: string; taso: string | null }) => void | Promise<void>;
}) {
  const [numero, setNumero] = useState("");
  const [lehti, setLehti] = useState("");
  const [lehdet, setLehdet] = useState<string[]>([]);
  const [peruste, setPeruste] = useState("");
  const [vihje, setVihje] = useState<string | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [kesken, setKesken] = useState(false);

  useEffect(() => {
    let voimassa = true;
    void (async () => {
      try {
        const v = await api.vapaaNumero(linjaId);
        if (!voimassa) return;
        setNumero(v.ehdotus);
        setLehdet(v.lehdet);
        /* ⛔ Esitäyttö VAIN kun vaihtoehtoja on yksi. Useammasta valinta
           olisi arvaus, ja väärä lehti tarkoittaa ettei numero varaudu
           siltä lehdeltä jolla sauma oikeasti kirjattaisiin — se on
           hiljainen epäonnistuminen, ei virheilmoitus. */
        if (v.lehdet.length === 1) setLehti(v.lehdet[0]!);
        /* Luku joka kertoo mihin ehdotus perustuu. Ilman sitä «8» on
           auktoritatiivisen näköinen arvaus — ja järjestelmä EI tiedä
           että 8 on olemassa, se tietää vain mitkä on kirjattu. */
        setVihje(
          v.numeroituja > 0
            ? t("het.vihje_suurin", { suurin: v.suurin ?? "" })
              + (v.numeroimattomia > 0
                ? t("het.vihje_numeroimattomia", { n: v.numeroimattomia })
                : t("het.vihje_tarkista"))
            : t("het.vihje_tyhja"));
      } catch { if (voimassa) setVihje(null); }
    })();
    return () => { voimassa = false; };
  }, [linjaId]);

  return (
    <div className="ikkuna">
      <div className="ikkuna__sisalto">
        <h2>{t("het.otsikko")}{linjaTunnus ? ` — ${linjaTunnus}` : ""}</h2>

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}

        <div className="lomake lomake--tiivis">
          <label style={{ flex: "0 0 200px" }}>{t("het.lehti")}
            <input value={lehti} onChange={(e) => setLehti(e.target.value)}
                   list="eitehda-lehdet" placeholder={lehdet.length ? t("het.valitse_lehti") : t("het.ei_lehtia")} />
            <datalist id="eitehda-lehdet">
              {lehdet.map((l) => <option key={l} value={l} />)}
            </datalist>
          </label>
          <label style={{ flex: "0 0 140px" }}>{t("het.numero")}
            <input value={numero} onChange={(e) => setNumero(e.target.value)}
                   inputMode="numeric" placeholder={t("het.numero_esim")} />
          </label>
          <label style={{ flex: "1 1 320px" }}>{t("het.peruste")}
            <input value={peruste} onChange={(e) => setPeruste(e.target.value)}
                   placeholder={t("het.peruste_esim")} />
          </label>
          <button className="nappi nappi--paa"
                  disabled={kesken || !numero.trim() || peruste.trim().length < 3}
                  onClick={() => void merkitse()}>{t("het.merkitse")}</button>
          <button className="nappi nappi--kevyt" onClick={sulje}>{t("het.peruuta")}</button>
        </div>

        {vihje && <p className="ikkuna__vihje">{vihje}</p>}

        {lehdet.length > 1 && !lehti.trim() && (
          <p className="huom" role="status">{t("het.monta_lehtea", { n: lehdet.length })}</p>
        )}
        <p className="ikkuna__vihje">{t("het.selite")}</p>
      </div>
    </div>
  );

  async function merkitse() {
    setKesken(true); setVirhe(null);
    try {
      const tulos = await api.hitsiaEiTehda(linjaId,
        { hitsinumero: numero.trim(), peruste: peruste.trim(),
          ...(lehti.trim() ? { piirustuslehti: lehti.trim() } : {}) });
      await valmis(tulos);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("het.virhe"));
    } finally { setKesken(false); }
  }
}
